import { useTheme } from "next-themes";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChartIcon as ChartBarIcon, RadarIcon } from "lucide-react";
import { useState } from "react";
import type { AssessmentScores } from "@/lib/assessment/scoring";

interface ScoreChartProps {
  scores: AssessmentScores;
  className?: string;
}

const ScoreChart = ({ scores, className }: ScoreChartProps) => {
  const [chartType, setChartType] = useState<"bar" | "radar">("bar");
  const { theme } = useTheme();

  const data = Object.entries(scores).map(([category, score]) => ({
    category: category.charAt(0).toUpperCase() + category.slice(1),
    score,
    fill: score >= 75 ? "#22c55e" : score >= 50 ? "#eab308" : "#ef4444"
  }));

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis 
          dataKey="category" 
          tick={{ fill: theme === "dark" ? "#fff" : "#000" }}
        />
        <YAxis 
          tick={{ fill: theme === "dark" ? "#fff" : "#000" }}
          domain={[0, 100]}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: theme === "dark" ? "#1a1b1e" : "#fff",
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: "6px",
          }}
        />
        <Legend />
        <Bar 
          dataKey="score" 
          name="Score (%)" 
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderRadarChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <RadarChart data={data}>
        <PolarGrid className="stroke-muted" />
        <PolarAngleAxis 
          dataKey="category"
          tick={{ fill: theme === "dark" ? "#fff" : "#000" }}
        />
        <PolarRadiusAxis 
          angle={30}
          domain={[0, 100]}
          tick={{ fill: theme === "dark" ? "#fff" : "#000" }}
        />
        <Radar
          name="Score (%)"
          dataKey="score"
          stroke="#2563eb"
          fill="#2563eb"
          fillOpacity={0.6}
        />
        <Legend />
      </RadarChart>
    </ResponsiveContainer>
  );

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Assessment Scores</CardTitle>
        <div className="flex gap-2">
          <Button
            variant={chartType === "bar" ? "default" : "outline"}
            size="sm"
            onClick={() => setChartType("bar")}
          >
            <ChartBarIcon className="h-4 w-4" />
          </Button>
          <Button
            variant={chartType === "radar" ? "default" : "outline"}
            size="sm"
            onClick={() => setChartType("radar")}
          >
            <RadarIcon className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {chartType === "bar" ? renderBarChart() : renderRadarChart()}
      </CardContent>
    </Card>
  );
};

export default ScoreChart;